import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Flame,
  Map,
  Leaf,
  TriangleAlert,
  Route,
  FileText,
  Users,
  Radio,
  ExternalLink,
  ArrowRight,
  Activity,
  AlertOctagon,
  Megaphone,
  CalendarDays,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CARDS = [
  {
    icon: Flame,
    eyebrow: "Rischio Incendi",
    title: "Monitora il rischio incendi",
    desc: "Consulta in tempo reale il livello di rischio incendi boschivi fornito dal LAMMA Toscana.",
    cta: "Apri mappa",
    href: "https://www.lamma.toscana.it/meteo/rischio-incendi",
    external: true,
    tint: "from-[#C15C3D] to-[#a64830]",
    accent: "#C15C3D",
    accentSoft: "rgba(193,92,61,0.18)",
    statusDot: "bg-[#C15C3D]",
    statusLabel: "Aggiornato dal LAMMA",
  },
  {
    icon: Map,
    eyebrow: "Difesa del suolo",
    title: "Geoportale ufficiale",
    desc: "Mappe ufficiali su frane, dissesto idrogeologico, vincoli, incendi e gestione del territorio.",
    cta: "Apri geoportale",
    href: "https://geoportale.lamma.rete.toscana.it/difesa_suolo/#/viewer/265",
    external: true,
    tint: "from-[#1E3A8A] to-[#152A66]",
    accent: "#2553B2",
    accentSoft: "rgba(37,83,178,0.18)",
    statusDot: "bg-[#3B82F6]",
    statusLabel: "Regione Toscana",
  },
  {
    icon: Leaf,
    eyebrow: "Habitat & Biodiversità",
    title: "Monte Pisano Natura 2000",
    desc: "Habitat, biodiversità, flora e fauna protetti del Monte Pisano.",
    cta: "Approfondisci",
    href: "/monte-pisano",
    external: false,
    tint: "from-[#2E4F3B] to-[#1f3828]",
    accent: "#2E4F3B",
    accentSoft: "rgba(46,79,59,0.18)",
    statusDot: "bg-[#7BA577]",
    statusLabel: "Rete Natura 2000",
  },
  {
    icon: TriangleAlert,
    eyebrow: "Segnalazioni cittadini",
    title: "Segnala una criticità",
    desc: "Alberi caduti, sentieri danneggiati, dissesti, situazioni di rischio: dicci cosa serve.",
    cta: "Invia segnalazione",
    href: "/segnalazioni",
    external: false,
    tint: "from-[#C19A3D] to-[#9c7e31]",
    accent: "#C19A3D",
    accentSoft: "rgba(193,154,61,0.18)",
    statusDot: "bg-[#E8C9A0]",
    statusLabel: "Risposta entro 48h",
  },
  {
    icon: Route,
    eyebrow: "Mappa sentieri",
    title: "Sentieri del Monte Pisano",
    desc: "Sentieri, percorsi e punti di interesse del Monte Pisano. In via di catalogazione.",
    cta: "Apri mappa",
    href: "/sentieri",
    external: false,
    tint: "from-[#5C8E5C] to-[#456C45]",
    accent: "#5C8E5C",
    accentSoft: "rgba(92,142,92,0.18)",
    statusDot: "bg-[#7BA577]",
    statusLabel: "In aggiornamento",
  },
  {
    icon: FileText,
    eyebrow: "Documenti utili",
    title: "Archivio documentale",
    desc: "Leggi, regolamenti, ordinanze, vincoli e documentazione tecnica del territorio.",
    cta: "Consulta",
    href: "/documenti",
    external: false,
    tint: "from-[#3F3F3F] to-[#1A1A1A]",
    accent: "#5C5C5C",
    accentSoft: "rgba(92,92,92,0.18)",
    statusDot: "bg-[#9A9A9A]",
    statusLabel: "Archivio pubblico",
  },
  {
    icon: Users,
    eyebrow: "Partecipa con noi",
    title: "Diventa volontario",
    desc: "Scopri come partecipare alle attività dell'Associazione Per Asciano sul territorio.",
    cta: "Diventa volontario",
    href: "/partecipa",
    external: false,
    tint: "from-[#1f3828] to-[#0f1a14]",
    accent: "#1f3828",
    accentSoft: "rgba(31,56,40,0.18)",
    statusDot: "bg-[#E8C9A0]",
    statusLabel: "Sempre aperto",
  },
];

const NOTICE_STYLES = {
  alert: {
    iconBg: "bg-[#C15C3D]/15 text-[#FCA98E]",
    border: "border-[#C15C3D]/40",
    label: "Allerta",
    labelBg: "bg-[#C15C3D] text-white",
    icon: AlertOctagon,
  },
  warning: {
    iconBg: "bg-[#C19A3D]/15 text-[#E8C9A0]",
    border: "border-[#C19A3D]/40",
    label: "Attenzione",
    labelBg: "bg-[#C19A3D] text-white",
    icon: TriangleAlert,
  },
  info: {
    iconBg: "bg-white/10 text-[#E8C9A0]",
    border: "border-white/20",
    label: "Comunicazione",
    labelBg: "bg-white/15 text-white",
    icon: Megaphone,
  },
  success: {
    iconBg: "bg-[#7BA577]/15 text-[#A7D095]",
    border: "border-[#7BA577]/40",
    label: "Novità",
    labelBg: "bg-[#5C8E5C] text-white",
    icon: CalendarDays,
  },
};

// Counter animato: anima il numero da 0 a "target" in ~1.2s al primo scroll-in view
const AnimatedCounter = ({ value, testid }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const dur = 1200;
            const start = performance.now();
            const target = Number(value) || 0;
            const step = (t) => {
              const p = Math.min(1, (t - start) / dur);
              const eased = 1 - Math.pow(1 - p, 3);
              setDisplay(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  return (
    <span ref={ref} data-testid={testid} className="tabular-nums">
      {display}
    </span>
  );
};

export const OperationalCenter = () => {
  const [metrics, setMetrics] = useState(null);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/operational-metrics`)
      .then((r) => r.json())
      .then(setMetrics)
      .catch(() => setMetrics({ segnalazioni_aperte: 0, interventi_effettuati: 0, volontari_attivi: 36 }));
    fetch(`${API}/api/notices`)
      .then((r) => r.json())
      .then((data) => setNotices(Array.isArray(data) ? data : []))
      .catch(() => setNotices([]));
  }, []);

  return (
    <section
      id="centro-operativo"
      data-testid="operational-center"
      className="relative bg-gradient-to-br from-[#0f1a14] via-[#152920] to-[#1f3828] text-white py-20 sm:py-28 overflow-hidden border-t border-[#2E4F3B]/60"
    >
      {/* Background atmosphere */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      <div className="pointer-events-none absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full bg-[#E8C9A0]/[0.05] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#7BA577]/[0.08] blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-3 rounded-full bg-white/8 border border-white/15 backdrop-blur px-4 py-2 mb-7">
            <span className="relative flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-[#7BA577] opacity-75 animate-ping" />
              <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-[#A7D095]" />
            </span>
            <Radio className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#E8C9A0]">
              Sala operativa · live
            </span>
          </div>

          <h2
            data-testid="operational-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight"
          >
            Centro Operativo
            <br />
            <span className="italic font-light text-[#E8C9A0]">del Territorio.</span>
          </h2>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl">
            Tutti gli strumenti utili per monitorare il Monte Pisano, prevenire i
            rischi e conoscere il territorio.
          </p>
        </div>

        {/* CARDS GRID */}
        <div className="mt-12 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {CARDS.map((c) => {
            const Icon = c.icon;
            const Wrapper = c.external ? "a" : Link;
            const wrapperProps = c.external
              ? { href: c.href, target: "_blank", rel: "noopener noreferrer" }
              : { to: c.href };
            return (
              <Wrapper
                key={c.title}
                {...wrapperProps}
                data-testid={`op-card-${c.eyebrow.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="group relative flex flex-col rounded-2xl bg-white/[0.04] backdrop-blur border border-white/10 p-6 sm:p-7 transition-all duration-300 hover:bg-white/[0.07] hover:border-white/25 hover:-translate-y-1 overflow-hidden"
              >
                {/* Subtle accent glow on hover */}
                <div
                  className="pointer-events-none absolute -top-20 -right-20 w-44 h-44 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ backgroundColor: c.accentSoft }}
                />

                {/* Top status row */}
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.tint} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] text-white/70">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.statusDot}`} />
                    {c.statusLabel}
                  </div>
                </div>

                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E8C9A0] mb-2">
                  {c.eyebrow}
                </div>
                <h3 className="font-display text-lg sm:text-xl font-black text-white leading-tight mb-3">
                  {c.title}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed flex-1">
                  {c.desc}
                </p>

                <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#E8C9A0] group-hover:gap-3 transition-all">
                  {c.cta}
                  {c.external ? <ExternalLink className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </div>
              </Wrapper>
            );
          })}
        </div>

        {/* STATO DEL TERRITORIO */}
        <div className="mt-14 sm:mt-20" data-testid="operational-status">
          <div className="flex items-center gap-3 mb-7">
            <Activity className="w-4 h-4 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#E8C9A0]">
              Stato del territorio
            </span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                label: "Segnalazioni aperte",
                value: metrics?.segnalazioni_aperte ?? 0,
                hint: "Da gestire",
                accent: "from-[#C15C3D] to-[#a64830]",
                dot: "bg-[#FCA98E]",
                testid: "metric-segnalazioni",
              },
              {
                label: "Interventi effettuati",
                value: metrics?.interventi_effettuati ?? 0,
                hint: "Sul territorio",
                accent: "from-[#7BA577] to-[#5C8E5C]",
                dot: "bg-[#A7D095]",
                testid: "metric-interventi",
              },
              {
                label: "Volontari attivi",
                value: metrics?.volontari_attivi ?? 0,
                hint: "Pronti a intervenire",
                accent: "from-[#E8C9A0] to-[#C19A3D]",
                dot: "bg-[#E8C9A0]",
                testid: "metric-volontari",
              },
            ].map((m) => (
              <div
                key={m.label}
                className="relative rounded-2xl bg-white/[0.04] backdrop-blur border border-white/10 p-6 sm:p-7 overflow-hidden"
              >
                <div className={`pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${m.accent} opacity-[0.18] blur-2xl`} />
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                  <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-white/65">
                    {m.hint}
                  </span>
                </div>
                <div className="font-display text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none tabular-nums">
                  <AnimatedCounter value={m.value} testid={m.testid} />
                </div>
                <div className="mt-4 text-sm sm:text-base text-white/80 font-semibold">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* INFORMAZIONI IMPORTANTI */}
        {notices.length > 0 && (
          <div className="mt-14 sm:mt-20" data-testid="operational-notices">
            <div className="flex items-center gap-3 mb-7">
              <Megaphone className="w-4 h-4 text-[#E8C9A0]" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#E8C9A0]">
                Informazioni importanti
              </span>
              <span className="flex-1 h-px bg-white/10" />
            </div>

            <div className="space-y-4">
              {notices.map((n) => {
                const style = NOTICE_STYLES[n.level] || NOTICE_STYLES.info;
                const NoticeIcon = style.icon;
                const external = n.cta_url && /^https?:/.test(n.cta_url);
                return (
                  <article
                    key={n.id}
                    data-testid={`notice-${n.id}`}
                    className={`rounded-2xl bg-white/[0.04] backdrop-blur border-2 ${style.border} p-5 sm:p-7 flex flex-col sm:flex-row gap-5 sm:items-center`}
                  >
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${style.iconBg}`}>
                      <NoticeIcon className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <div className={`inline-block text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.22em] px-2 py-1 rounded ${style.labelBg} mb-2`}>
                        {style.label}
                      </div>
                      <h3 className="font-display text-lg sm:text-xl font-black text-white leading-tight">
                        {n.title}
                      </h3>
                      <p className="mt-2 text-sm sm:text-base text-white/75 leading-relaxed">
                        {n.message}
                      </p>
                    </div>
                    {n.cta_url && n.cta_label && (
                      <a
                        href={n.cta_url}
                        target={external ? "_blank" : undefined}
                        rel={external ? "noopener noreferrer" : undefined}
                        data-testid={`notice-cta-${n.id}`}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#1A1A1A] hover:bg-[#E8C9A0] font-bold text-sm px-5 py-3 transition-colors shrink-0"
                      >
                        {n.cta_label}
                        {external ? <ExternalLink className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
